import { useNavigate } from 'react-router-dom';
import { ShieldCheck, SquarePen, User } from 'lucide-react';
import { useViewModeStore, type ViewMode } from '@/stores/viewMode';
import { cn } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ADMIN VIEW ↔ EMPLOYEE VIEW
 *
 * An administrator has two jobs, and until now both were on one menu: the
 * admin sections at the top, their own learning folded into a section at the
 * bottom. This splits them.
 *
 * It changes the whole screen, not just the list of links — employee view
 * gives the learner dashboard as well as the learner menu, because the
 * question this exists to answer is "what does my team actually see", and half
 * an answer to that is worse than none.
 *
 * ── It offers, it does not allow ───────────────────────────────────────────
 *
 * Every admin route stays reachable by URL in employee view, and the API
 * enforces exactly the same rules in both. A menu has never been a security
 * control, and building one that looks like a permission is how people start
 * trusting it as one.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * The working half of the switch, which is not the same for everybody.
 *
 * `admin` mode is the MODE name and stays — it is what the store and the
 * menu-folding key off. What it is CALLED and drawn as depends on whose
 * account it is: a shield for an administrator, a pen for a trainer, who
 * writes training rather than governing the system. Showing a Trainer the
 * shield said they were something they are not, on the one control whose job
 * is to say which of their two jobs they are doing.
 */
const WORKING_VIEW: Record<'admin' | 'trainer', { icon: typeof User; label: string }> = {
  admin: { icon: ShieldCheck, label: 'Admin view' },
  trainer: { icon: SquarePen, label: 'Trainer view' },
};

/**
 * No `collapsed` variant any more.
 *
 * It existed for the sidebar rail, where two buttons had to stack to fit 68px.
 * The switch lives in the top bar now, which is the same width on every page,
 * so a second layout would be a branch nothing takes.
 */
export function ViewSwitch({ role = 'admin' }: { role?: 'admin' | 'trainer' }) {
  const working = WORKING_VIEW[role];

  const VIEWS: { mode: ViewMode; icon: typeof User; label: string }[] = [
    { mode: 'admin', icon: working.icon, label: working.label },
    { mode: 'employee', icon: User, label: 'Employee view' },
  ];

  const mode = useViewModeStore((s) => s.mode);
  const setMode = useViewModeStore((s) => s.setMode);
  const navigate = useNavigate();

  const switchTo = (next: ViewMode) => {
    if (next === mode) return;
    setMode(next);
    /**
     * Home, every time.
     *
     * Switching to employee view while standing on /admin/users would leave
     * somebody on a page their new menu does not contain — the sidebar and the
     * content disagreeing about which product this is. The dashboard is the
     * one page that exists in both and differs between them, so it is also the
     * clearest possible demonstration that the switch did something.
     */
    navigate('/');
  };

  return (
    /*
      Two icons, and only as wide as two icons.
      ───────────────────────────────────────────────────────────────────────
      This was a full-width segmented control with the words "Admin" and
      "Employee" on it, sitting on the user card — a control used a few times
      a week, drawn larger than the person's own name.

      `w-fit` survives the move to the top bar: it stops the pill stretching in
      whatever flex row it is dropped into, which is what made it oversized in
      the sidebar however small the buttons were.

      Sized to sit with the bell and the theme toggle beside it: 28px buttons
      in a 32px pill, against their 36px. Slightly smaller on purpose — it is a
      mode, not an action, and it should not compete with them.

      The words are gone from the surface but not from the page — `title`
      carries them on hover and `aria-label` carries them to a screen reader,
      which is the trade an icon-only control has to make good on.
    */
    <div
      role="group"
      aria-label={`Switch between ${working.label.toLowerCase()} and employee view`}
      className="flex w-fit items-center gap-0.5 rounded-lg bg-muted/70 p-0.5"
    >
      {VIEWS.map((view) => {
        const Icon = view.icon;
        const active = view.mode === mode;

        return (
          <button
            key={view.mode}
            type="button"
            onClick={() => switchTo(view.mode)}
            title={view.label}
            aria-label={view.label}
            aria-pressed={active}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
          </button>
        );
      })}
    </div>
  );
}
